import { useEffect, useRef, useCallback, useState } from "react";
import "./MonitorAlarmTable.scss";
import priority_high_svg from "../../../common/icons/svg/priority-high.svg";
import priority_medium_svg from "../../../common/icons/svg/priority-medium.svg";
import priority_low_svg from "../../../common/icons/svg/priority-low.svg";
import { getAlarmHistory } from "../../../helper/alarm-helper";
import moment from "moment";
import { SET_PARAMETERS, MANEUVERS } from "../../../common/ENUMS/PARAMETER_ENUM";
import filter from "../../../common/images/filter.png";
import { ALARM_TYPE_FILTER, ALARM_PRIORITY_FILTER } from "../../../common/constant/common-constants";
import { convertUTCtoIST } from "../../../common/utils/CommonUtils";

export function MonitorAlarmTable(props) {
    const [data, setData] = useState([]);
    const [activeAlarm, setActiveAlarm] = useState([]);
    const [endOffPage, setEndOffPage] = useState(false);
    const [createdAt, setCreatedAt] = useState(null);
    const [offset, setOffset] = useState(null);
    const tableRef = useRef(null);
    const isFetchingRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);


    const [filterDropdown, setFilterDropdown] = useState({ open: false, column: null });
    const [selectedTypeFilter, setSelectedTypeFilter] = useState(ALARM_TYPE_FILTER?.[0]?.name);
    const [selectedPriorityFilter, setPrioritySelectedFilter] = useState(ALARM_PRIORITY_FILTER?.[0]?.name);
    const dropdownRef = useRef(null);

    const resetState = () => {
        setEndOffPage(false);
        setData([]);
        setCreatedAt(null);
        setOffset(null);
    }

    const getPMAlarm = (created, offs, selectedTypeFilter, selectedPriorityFilter) => {
        if (endOffPage) return;
        setIsLoading(true)
        getAlarmHistory(
            {
                bedId: props.bedId,
                typefilter: selectedTypeFilter,
                priorityfilter: selectedPriorityFilter,
                createdAt: created ? created?.toString() : null,
                limit: 100,
                offset: offs ? offs : null,
            }
        ).then((res) => {
            if (res?.alarms?.length < 100) setEndOffPage(true);
            const PMAlarms = res.alarms;
            if (PMAlarms) {
                if (!created) setCreatedAt(PMAlarms?.[0]?.createdAt);
                setData((prevData) => {
                    setOffset(prevData.length + PMAlarms.length);
                    return [...prevData, ...PMAlarms]
                });
            }
        }).finally(() => {
            isFetchingRef.current = false;
            setIsLoading(false)
        })
            .catch((err) => {
                console.log("alarm err: ", err);
            });
    }

    const handleScroll = useCallback(() => {
        const table = tableRef.current;
        if (table) {
            const scrollPosition = table.scrollTop + table.clientHeight;
            const isLastRows = scrollPosition >= table.scrollHeight - table.clientHeight * 0.1;

            if (isLastRows && !isFetchingRef.current && offset) {
                isFetchingRef.current = true;
                getPMAlarm(createdAt, offset, selectedTypeFilter, selectedPriorityFilter);
            }
        }
    }, [offset]);



    useEffect(() => {
        const table = tableRef.current;
        if (table) {
            table.addEventListener('scroll', handleScroll);
        }

        return () => {
            if (table) {
                table.removeEventListener('scroll', handleScroll);
            }
        };
    }, [handleScroll]);


    useEffect(() => {
        if (!offset) getPMAlarm(null, null, selectedTypeFilter, selectedPriorityFilter);
    }, [offset])


    useEffect(() => {
        let activeAl = [];
        let activeAlarmsPM = [...props.PMAlarmData.PM_alarms.alarms_physiological, ...props.PMAlarmData.PM_alarms.alarms__technical]
        activeAlarmsPM?.forEach(e => {

            activeAl.push({
                category: null,
                createdAt: null,
                message: e.message,
                activeTime: moment(e.time).format("YYYY-MM-DD HH:mm:ss"),
                priority: e.priority,
                resolvedTime: null,
                subcategory: null,
                type: e.type,
                status: 'active'
            })
        });
        setActiveAlarm(activeAl);
    }, [props.PMAlarmData]);

    useEffect(() => {
        resetState();
    }, [props.bedId, selectedTypeFilter, selectedPriorityFilter]);

    const toggleFilterDropdown = (column) => {
        setFilterDropdown((prev) => ({
            open: !(prev.open && prev.column === column),
            column,
        }));
    };

    const handleFilterSelect = (filter, data) => {
        if (data === "TYPE") {
            setSelectedTypeFilter(filter.name);
            setPrioritySelectedFilter(ALARM_PRIORITY_FILTER?.[0]?.name)
        }
        if (data === "PRIORITY") {
            setPrioritySelectedFilter(filter.name)
        };
        setFilterDropdown({ open: false, column: null });
    }

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setFilterDropdown({ open: false, column: null });
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    const getPriority = (priority) => {
        let pr = priority?.toUpperCase();
        switch (pr) {
            case "HIGH":
                return { icon: priority_high_svg, color: '#AD0606', textColor: '#fff' };
            case "MEDIUM":
                return { icon: priority_medium_svg, color: '#FCC331', textColor: '#000' };
            case "LOW":
                return { icon: priority_low_svg, color: '#3ADAE5', textColor: '#000' };
            default:
                return { icon: '', color: '#3ADAE5' };
        }
    }

    let livealarms = [...activeAlarm].sort((a, b) => new Date(b.activeTime) - new Date(a.activeTime))
    let sortedEvents = (selectedTypeFilter === 'ACTIVE') ? [...livealarms] : [...livealarms, ...data] || [];

    return (
        <>
            <div className="alarm-history-text">Event History</div>
            <div className="alarm-history-data-container">
                <div ref={tableRef} className="alarm-table-container">
                    <table className="alarm-table">
                        <thead className="sticky">
                            <tr>
                                {props?.header?.map((data, count) => {
                                    // determine if we should show the PRIORITY filter
                                    const canShowPriorityFilter =
                                        selectedTypeFilter === "PHYSIOLOGICAL" ||
                                        selectedTypeFilter === "TECHNICAL";

                                    // skip rendering PRIORITY filter when not allowed
                                    const isPriorityColumn = data === "PRIORITY";
                                    const isTypeColumn = data === "TYPE";
                                    const shouldShowFilter =
                                        isTypeColumn || (isPriorityColumn && canShowPriorityFilter);

                                    return (
                                        <th
                                            key={count}
                                            className="alarm-header"
                                            style={{ position: "relative" }}
                                        >
                                            <div
                                                className="header-content"
                                                style={{
                                                    cursor: shouldShowFilter ? "pointer" : "default",
                                                }}
                                                onClick={
                                                    shouldShowFilter
                                                        ? () => toggleFilterDropdown(data)
                                                        : undefined
                                                }
                                            >
                                                {data}
                                                {shouldShowFilter && (
                                                    <span className="filter-icon">
                                                        <img src={filter} alt="filter" width="21vw" />
                                                    </span>
                                                )}
                                            </div>

                                            {/* filter dropdown */}
                                            {filterDropdown.open &&
                                                filterDropdown.column === data &&
                                                shouldShowFilter && (
                                                    <div
                                                        ref={dropdownRef}
                                                        className="filter-dropdown"
                                                        style={{
                                                            position: "absolute",
                                                            top: "100%",
                                                            left: 0,
                                                            zIndex: 10,
                                                            minWidth: "9.8vw",
                                                            marginTop: "0.5vh",
                                                        }}
                                                    >
                                                        {(data === "TYPE"
                                                            ? ALARM_TYPE_FILTER
                                                            : ALARM_PRIORITY_FILTER
                                                        ).map((f) => (
                                                            <div
                                                                key={f.id}
                                                                onClick={() => handleFilterSelect(f, data)}
                                                                style={{
                                                                    padding: "0.7vw 2vh",
                                                                    cursor: "pointer",
                                                                    border: "none",
                                                                    fontSize: "0.8vw",
                                                                    lineHeight: "20px",
                                                                    border: "0.6vw solid #333333",
                                                                    background:
                                                                        data === "TYPE"
                                                                            ? f.name === selectedTypeFilter
                                                                                ? "#6D1DF0"
                                                                                : "#333333"
                                                                            : f.name === selectedPriorityFilter
                                                                                ? "#6D1DF0"
                                                                                : "#333333",
                                                                }}
                                                            >
                                                                {f.label}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>

                        <tbody>

                            {
                                sortedEvents?.map((row, count) => {
                                    if (!row) return null;

                                    const activeTime = row?.activeTime || '';
                                    const resolveTime = row?.resolvedTime || '';

                                    const activeIST = convertUTCtoIST(activeTime);
                                    const resolveIST = convertUTCtoIST(resolveTime);
                                    const priorityData = row?.priority ? getPriority(row.priority) : { color: '', textColor: '', icon: '' };
                                    const color = row?.status === 'active' ? priorityData.color : '';
                                    const textColor = row?.status === 'active' ? priorityData.textColor : '';

                                    // Safe type formatting
                                    let type = '';
                                    if (row?.type) {
                                        type = row.type
                                            .split(' ')
                                            .map((t) => t ? t[0].toUpperCase() + t.substring(1).toLowerCase() : '')
                                            .join(' ');
                                    }

                                    // Skip if no message
                                    if (!row?.message) return null;

                                    return (
                                        <tr key={count}>
                                            <td style={{ backgroundColor: color, color: textColor, fontFamily: "Noto Sans", fontWeight: 400, fontStyle: "normal", fontSize: "0.83vw" }}>
                                                {type || '-'}
                                            </td>
                                            <td style={{ backgroundColor: color }}>
                                                <div className="alarm-priority">
                                                    {priorityData.icon && <img alt="priority icon" src={priorityData.icon} className="priority" width="31vw" />}
                                                </div>
                                            </td>
                                            <td style={{ backgroundColor: color, color: textColor }}>
                                                <div className="alarm-name" style={{ fontFamily: "Noto Sans", fontWeight: 400, fontStyle: "normal", fontSize: "0.83vw" }}>
                                                    {row.message || '-'}
                                                </div>
                                            </td>
                                            <td style={{ backgroundColor: color, color: textColor }}>
                                                <div className="alarm-time" style={{ fontFamily: "Noto Sans", fontWeight: 400, fontSize: "0.83vw" }}>
                                                    {activeIST.time}
                                                </div>
                                                <div className="alarm-date" style={{ fontFamily: "Noto Sans", fontWeight: 400, fontSize: "0.73vw" }}>
                                                    {activeIST.date}
                                                </div>
                                            </td>

                                            <td style={{ backgroundColor: color, color: textColor }}>
                                                <div className="alarm-time" style={{ fontFamily: "Noto Sans", fontWeight: 400, fontSize: "0.83vw" }}>
                                                    {resolveIST.time}
                                                </div>
                                                <div className="alarm-date" style={{ fontFamily: "Noto Sans", fontWeight: 400, fontSize: "0.73vw", }}>
                                                    {resolveIST.date}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            }

                        </tbody>
                    </table>
                    {isLoading && (
                        <div className="spinner-container">
                            <div className="spinner"></div>
                        </div>
                    )}
                </div>
            </div>
        </>


    );

}